import { ACHIEVEMENTS } from '../types/gamification';
import { PomodoroRecord } from '../types/project';

type AchievementProgress = { [key: string]: { progress: number; unlockedAt?: Date } };

const updateAchievement = (
  id: string,
  progress: number,
  maxProgress: number,
  current: AchievementProgress
): { progress: number; unlockedAt?: Date } => ({
  progress: Math.min(progress, maxProgress),
  unlockedAt: progress >= maxProgress && !current[id]?.unlockedAt
    ? new Date()
    : current[id]?.unlockedAt,
});

// Convert duration to minutes (handles mixed seconds/minutes DB format)
const toMinutes = (duration: number): number => {
  if (!duration || duration <= 0) return 0;
  if (duration > 300) return Math.round(duration / 60);
  return duration;
};

export class AchievementService {
  static checkPomodoroAchievements(
    pomodoros: PomodoroRecord[],
    currentAchievements: AchievementProgress
  ): AchievementProgress {
    const updates: AchievementProgress = {};
    const completed = pomodoros.filter(p => p.completed);
    const count = completed.length;

    // --- Milestone achievements ---
    updates.FIRST_POMODORO = updateAchievement('FIRST_POMODORO', count, 1, currentAchievements);
    updates.FIVE_SESSIONS = updateAchievement('FIVE_SESSIONS', count, 5, currentAchievements);
    updates.TEN_SESSIONS = updateAchievement('TEN_SESSIONS', count, 10, currentAchievements);
    updates.TWENTY_FIVE_SESSIONS = updateAchievement('TWENTY_FIVE_SESSIONS', count, 25, currentAchievements);
    updates.FIFTY_SESSIONS = updateAchievement('FIFTY_SESSIONS', count, 50, currentAchievements);
    updates.PRODUCTIVITY_MASTER = updateAchievement('PRODUCTIVITY_MASTER', count, 100, currentAchievements);
    updates.TWO_FIFTY_SESSIONS = updateAchievement('TWO_FIFTY_SESSIONS', count, 250, currentAchievements);
    updates.FIVE_HUNDRED_SESSIONS = updateAchievement('FIVE_HUNDRED_SESSIONS', count, 500, currentAchievements);
    updates.THOUSAND_SESSIONS = updateAchievement('THOUSAND_SESSIONS', count, 1000, currentAchievements);

    // --- Time-based achievements (total focus minutes) ---
    const totalMinutes = completed.reduce((sum, p) => sum + toMinutes(p.duration), 0);
    updates.ONE_HOUR_TOTAL = updateAchievement('ONE_HOUR_TOTAL', totalMinutes, 60, currentAchievements);
    updates.TEN_HOURS_TOTAL = updateAchievement('TEN_HOURS_TOTAL', totalMinutes, 600, currentAchievements);
    updates.FIFTY_HOURS_TOTAL = updateAchievement('FIFTY_HOURS_TOTAL', totalMinutes, 3000, currentAchievements);
    updates.HUNDRED_HOURS_TOTAL = updateAchievement('HUNDRED_HOURS_TOTAL', totalMinutes, 6000, currentAchievements);
    updates.FIVE_HUNDRED_HOURS = updateAchievement('FIVE_HUNDRED_HOURS', totalMinutes, 30000, currentAchievements);

    // --- Time-of-day achievements ---
    const earlyCount = completed.filter(p => p.startTime && new Date(p.startTime).getHours() < 9).length;
    const nightCount = completed.filter(p => p.startTime && new Date(p.startTime).getHours() >= 20).length;
    updates.EARLY_BIRD = updateAchievement('EARLY_BIRD', earlyCount, 5, currentAchievements);
    updates.NIGHT_OWL = updateAchievement('NIGHT_OWL', nightCount, 5, currentAchievements);

    // --- Session length achievements ---
    const hasLongSession = completed.some(p => toMinutes(p.duration) >= 45);
    const hasMarathonSession = completed.some(p => toMinutes(p.duration) >= 90);
    updates.LONG_SESSION = updateAchievement('LONG_SESSION', hasLongSession ? 1 : 0, 1, currentAchievements);
    updates.MARATHON_SESSION = updateAchievement('MARATHON_SESSION', hasMarathonSession ? 1 : 0, 1, currentAchievements);

    // --- Daily volume achievements ---
    const dailyCounts = new Map<string, number>();
    const weekendDays = new Set<number>(); // 0=Sun, 6=Sat
    for (const p of completed) {
      if (!p.startTime) continue;
      const d = new Date(p.startTime);
      const key = d.toDateString();
      dailyCounts.set(key, (dailyCounts.get(key) || 0) + 1);
      const day = d.getDay();
      if (day === 0 || day === 6) weekendDays.add(day);
    }
    const maxDaily = Math.max(0, ...dailyCounts.values());
    updates.FIVE_IN_A_DAY = updateAchievement('FIVE_IN_A_DAY', maxDaily >= 5 ? 1 : 0, 1, currentAchievements);
    updates.TEN_IN_A_DAY = updateAchievement('TEN_IN_A_DAY', maxDaily >= 10 ? 1 : 0, 1, currentAchievements);
    updates.WEEKEND_WARRIOR = updateAchievement('WEEKEND_WARRIOR', weekendDays.size >= 2 ? 1 : 0, 1, currentAchievements);

    // --- Project achievements ---
    const projectIds = new Set(completed.filter(p => p.projectId).map(p => p.projectId));
    updates.FIRST_PROJECT = updateAchievement('FIRST_PROJECT', projectIds.size >= 1 ? 1 : 0, 1, currentAchievements);
    updates.MULTI_PROJECT = updateAchievement('MULTI_PROJECT', projectIds.size, 3, currentAchievements);

    return updates;
  }

  static checkLearnAchievements(
    stats: { videosAnalyzed: number; questionsAsked: number; cardsReviewed: number; longestVideoSec: number },
    currentAchievements: AchievementProgress
  ): AchievementProgress {
    const updates: AchievementProgress = {};

    updates.FIRST_VIDEO = updateAchievement('FIRST_VIDEO', stats.videosAnalyzed, 1, currentAchievements);
    updates.TEN_VIDEOS = updateAchievement('TEN_VIDEOS', stats.videosAnalyzed, 10, currentAchievements);
    updates.FIFTY_VIDEOS = updateAchievement('FIFTY_VIDEOS', stats.videosAnalyzed, 50, currentAchievements);

    updates.CURIOUS_MIND = updateAchievement('CURIOUS_MIND', stats.questionsAsked, 25, currentAchievements);
    updates.HUNDRED_QUESTIONS = updateAchievement('HUNDRED_QUESTIONS', stats.questionsAsked, 100, currentAchievements);

    updates.SPACED_REPETITION = updateAchievement('SPACED_REPETITION', stats.cardsReviewed, 50, currentAchievements);
    updates.RETENTION_MASTER = updateAchievement('RETENTION_MASTER', stats.cardsReviewed, 250, currentAchievements);

    updates.LONG_LECTURE = updateAchievement('LONG_LECTURE', stats.longestVideoSec >= 3600 ? 1 : 0, 1, currentAchievements);

    return updates;
  }

  static checkStreakAchievements(
    currentStreak: number,
    currentAchievements: AchievementProgress
  ): AchievementProgress {
    const updates: AchievementProgress = {};

    updates.THREE_DAY_STREAK = updateAchievement('THREE_DAY_STREAK', currentStreak, 3, currentAchievements);
    updates.STREAK_MASTER = updateAchievement('STREAK_MASTER', currentStreak, 7, currentAchievements);
    updates.FOURTEEN_DAY_STREAK = updateAchievement('FOURTEEN_DAY_STREAK', currentStreak, 14, currentAchievements);
    updates.MONTHLY_MASTER = updateAchievement('MONTHLY_MASTER', currentStreak, 30, currentAchievements);
    updates.SIXTY_DAY_STREAK = updateAchievement('SIXTY_DAY_STREAK', currentStreak, 60, currentAchievements);
    updates.HUNDRED_DAY_STREAK = updateAchievement('HUNDRED_DAY_STREAK', currentStreak, 100, currentAchievements);

    return updates;
  }

  static calculateExperience(achievements: { [key: string]: { progress: number } }): number {
    return Object.entries(achievements)
      .filter(([id, data]) => {
        const achievement = ACHIEVEMENTS[id as keyof typeof ACHIEVEMENTS];
        return achievement && data.progress >= achievement.maxProgress;
      })
      .reduce((total, [id]) => {
        return total + ACHIEVEMENTS[id as keyof typeof ACHIEVEMENTS].xp;
      }, 0);
  }
}
